/**
 * MinistrareIA - WhatsApp Business Media Components
 * Renders media messages (image, audio, video, document) in WhatsApp Web style
 *
 * Uses direct URLs (msg.media_url) instead of base64/thumbnails
 */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Image as ImageIcon,
  FileText,
  File,
  Play,
  Pause,
  Download,
  X,
  Loader2,
  ZoomIn,
  AlertCircle,
  Mic,
  Video,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const size = typeof bytes === 'object' ? bytes.low || 0 : bytes;
  if (size === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getDocumentIcon = (mimetype, fileName) => {
  const mime = (mimetype || '').toLowerCase();
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';

  if (mime.includes('pdf') || ext === 'pdf') {
    return { icon: FileText, color: '#E53935', label: 'PDF' };
  }
  if (mime.includes('word') || ['doc', 'docx'].includes(ext)) {
    return { icon: FileText, color: '#2196F3', label: 'DOC' };
  }
  if (mime.includes('excel') || mime.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) {
    return { icon: FileText, color: '#4CAF50', label: 'XLS' };
  }
  if (mime.includes('powerpoint') || mime.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
    return { icon: FileText, color: '#FF9800', label: 'PPT' };
  }
  if (mime.includes('zip') || mime.includes('rar') || ['zip', 'rar', '7z'].includes(ext)) {
    return { icon: File, color: '#9C27B0', label: 'ZIP' };
  }
  if (mime.includes('text') || ext === 'txt') {
    return { icon: FileText, color: '#607D8B', label: 'TXT' };
  }
  return { icon: File, color: '#757575', label: 'FILE' };
};

// ============================================
// IMAGE MESSAGE
// ============================================

const WABusinessImageMessage = ({ mediaUrl, caption, isSent, onOpenLightbox }) => {
  const { colors } = useTheme();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const styles = {
    container: {
      position: 'relative',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      maxWidth: 330,
      minWidth: 200,
      backgroundColor: colors.bgTertiary,
    },
    image: {
      width: '100%',
      maxHeight: 320,
      objectFit: 'cover',
      display: loaded ? 'block' : 'none',
      borderRadius: 8,
    },
    placeholder: {
      width: '100%',
      height: 200,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      color: colors.textTertiary,
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.4))',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      padding: 8,
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },
    zoomIcon: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    caption: {
      padding: '6px 0 0',
      fontSize: 14,
      color: isSent ? colors.messageSentText : colors.messageReceivedText,
      wordBreak: 'break-word',
      lineHeight: 1.4,
    },
  };

  const handleClick = () => {
    if (!error && onOpenLightbox) {
      onOpenLightbox({ type: 'image', url: mediaUrl, caption });
    }
  };

  return (
    <div>
      <div
        style={styles.container}
        onClick={handleClick}
        onMouseEnter={(e) => {
          const overlay = e.currentTarget.querySelector('.wa-biz-img-overlay');
          if (overlay) overlay.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const overlay = e.currentTarget.querySelector('.wa-biz-img-overlay');
          if (overlay) overlay.style.opacity = '0';
        }}
      >
        {!error ? (
          <>
            {!loaded && (
              <div style={styles.placeholder}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            )}
            <img
              src={mediaUrl}
              alt={caption || 'Imagem'}
              style={styles.image}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              onError={() => { setError(true); setLoaded(false); }}
            />
            {loaded && (
              <div className="wa-biz-img-overlay" style={styles.overlay}>
                <div style={styles.zoomIcon}>
                  <ZoomIn size={18} />
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={styles.placeholder}>
            <AlertCircle size={32} />
            <span style={{ fontSize: 12 }}>Imagem indisponível</span>
          </div>
        )}
      </div>
      {caption && <p style={styles.caption}>{caption}</p>}
    </div>
  );
};

// ============================================
// AUDIO MESSAGE
// ============================================

const WABusinessAudioMessage = ({ mediaUrl, isSent }) => {
  const { colors } = useTheme();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const waves = useMemo(
    () => Array(32).fill(0).map(() => Math.random() * 0.8 + 0.2),
    []
  );

  const progress = duration > 0 ? currentTime / duration : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onError = () => {
      setError('Áudio indisponível');
      setIsLoading(false);
      setIsPlaying(false);
    };
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
    };
  }, []);

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || error) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audio.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
      }).catch(() => {
        setIsLoading(false);
        setError('Não foi possível reproduzir');
      });
    }
  }, [isPlaying, error]);

  const handleWaveformClick = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  }, [duration]);

  const sentBg = isSent ? 'rgba(255,255,255,0.2)' : colors.accentPrimary;
  const sentColor = isSent ? colors.messageSentText : 'white';

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 4px',
      minWidth: 240,
      maxWidth: 340,
    },
    playButton: {
      width: 42,
      height: 42,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: sentBg,
      color: sentColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: error ? 'not-allowed' : 'pointer',
      flexShrink: 0,
      transition: 'transform 0.1s ease, opacity 0.15s ease',
      opacity: error ? 0.5 : 1,
    },
    waveformOuter: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    waveform: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: 30,
      cursor: duration > 0 ? 'pointer' : 'default',
    },
    bar: {
      width: 3,
      borderRadius: 2,
      transition: 'background-color 0.1s ease',
    },
    timeRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 11,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
    },
    micLabel: {
      fontSize: 10,
      color: isSent ? 'rgba(255,255,255,0.6)' : colors.textTertiary,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
  };

  return (
    <div style={styles.container}>
      <audio ref={audioRef} src={mediaUrl} preload="metadata" />

      <button style={styles.playButton} onClick={handlePlayPause} disabled={!!error}>
        {isLoading ? (
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        ) : isPlaying ? (
          <Pause size={20} />
        ) : (
          <Play size={20} style={{ marginLeft: 2 }} />
        )}
      </button>

      <div style={styles.waveformOuter}>
        <div style={styles.waveform} onClick={handleWaveformClick}>
          {waves.map((value, index) => {
            const isPlayed = (index / waves.length) <= progress;
            const barColor = isPlayed
              ? (isSent ? 'rgba(255,255,255,0.9)' : colors.accentPrimary)
              : (isSent ? 'rgba(255,255,255,0.35)' : (colors.textTertiary + '80'));
            return (
              <div
                key={index}
                style={{
                  ...styles.bar,
                  height: `${Math.max(value * 26, 4)}px`,
                  backgroundColor: barColor,
                }}
              />
            );
          })}
        </div>

        <div style={styles.timeRow}>
          <span>{formatDuration(isPlaying || currentTime > 0 ? currentTime : 0)}</span>
          <span>{formatDuration(duration)}</span>
        </div>

        {error ? (
          <div style={{ ...styles.micLabel, color: '#ef4444' }}>
            <AlertCircle size={10} />
            <span>{error}</span>
          </div>
        ) : (
          <div style={styles.micLabel}>
            <Mic size={10} />
            <span>Mensagem de voz</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// VIDEO MESSAGE
// ============================================

const WABusinessVideoMessage = ({ mediaUrl, caption, isSent, onOpenLightbox }) => {
  const { colors } = useTheme();
  const [showPlayer, setShowPlayer] = useState(false);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);

  const styles = {
    container: {
      position: 'relative',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      maxWidth: 330,
      minWidth: 200,
      backgroundColor: '#000',
    },
    video: {
      width: '100%',
      maxHeight: 320,
      display: 'block',
      borderRadius: 8,
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)',
    },
    playButton: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      border: '2px solid rgba(255,255,255,0.8)',
    },
    errorContainer: {
      width: '100%',
      height: 180,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      color: colors.textTertiary,
      backgroundColor: colors.bgTertiary,
      borderRadius: 8,
    },
    caption: {
      padding: '6px 0 0',
      fontSize: 14,
      color: isSent ? colors.messageSentText : colors.messageReceivedText,
      wordBreak: 'break-word',
      lineHeight: 1.4,
    },
  };

  const handleClick = () => {
    if (error) return;
    if (onOpenLightbox) {
      onOpenLightbox({ type: 'video', url: mediaUrl, caption });
    } else {
      setShowPlayer(true);
    }
  };

  if (error) {
    return (
      <div>
        <div style={styles.errorContainer}>
          <AlertCircle size={32} />
          <span style={{ fontSize: 12 }}>Vídeo indisponível</span>
        </div>
        {caption && <p style={styles.caption}>{caption}</p>}
      </div>
    );
  }

  return (
    <div>
      <div style={styles.container}>
        {showPlayer ? (
          <video
            ref={videoRef}
            src={mediaUrl}
            controls
            autoPlay
            style={styles.video}
            onError={() => setError(true)}
          />
        ) : (
          <div onClick={handleClick}>
            <video
              src={mediaUrl}
              preload="metadata"
              style={styles.video}
              muted
              onError={() => setError(true)}
            />
            <div style={styles.overlay}>
              <div style={styles.playButton}>
                <Play size={28} style={{ marginLeft: 3 }} />
              </div>
            </div>
          </div>
        )}
      </div>
      {caption && <p style={styles.caption}>{caption}</p>}
    </div>
  );
};

// ============================================
// DOCUMENT MESSAGE
// ============================================

const WABusinessDocumentMessage = ({ mediaUrl, filename, isSent }) => {
  const { colors } = useTheme();
  const displayName = filename || 'Documento';
  const { icon: DocIcon, color: iconColor, label } = getDocumentIcon('', displayName);

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 12px',
      backgroundColor: isSent ? 'rgba(255,255,255,0.1)' : colors.bgTertiary,
      borderRadius: 8,
      minWidth: 240,
      maxWidth: 340,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      textDecoration: 'none',
      color: 'inherit',
    },
    iconContainer: {
      width: 44,
      height: 44,
      borderRadius: 8,
      backgroundColor: `${iconColor}20`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    info: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    fileName: {
      fontSize: 14,
      fontWeight: 500,
      color: isSent ? colors.messageSentText : colors.textPrimary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
    },
    downloadBtn: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'transparent',
      color: isSent ? colors.messageSentText : colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
    },
  };

  const hoverBgIn = isSent ? 'rgba(255,255,255,0.15)' : colors.bgSecondary;
  const hoverBgOut = isSent ? 'rgba(255,255,255,0.1)' : colors.bgTertiary;

  return (
    <a
      href={mediaUrl}
      target="_blank"
      rel="noopener noreferrer"
      download={displayName}
      style={styles.container}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBgIn; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = hoverBgOut; }}
    >
      <div style={styles.iconContainer}>
        <DocIcon size={24} color={iconColor} />
      </div>

      <div style={styles.info}>
        <span style={styles.fileName} title={displayName}>
          {displayName}
        </span>
        <div style={styles.meta}>
          <span>{label}</span>
        </div>
      </div>

      <div style={styles.downloadBtn}>
        <Download size={20} />
      </div>
    </a>
  );
};

// ============================================
// MEDIA LIGHTBOX
// ============================================

const WABusinessMediaLightbox = ({ isOpen, onClose, media }) => {
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen || !media) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.95)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '16px 20px',
      gap: 12,
    },
    headerBtn: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      overflow: 'hidden',
    },
    media: {
      maxWidth: '90%',
      maxHeight: '85vh',
      objectFit: 'contain',
      borderRadius: 4,
    },
    caption: {
      padding: '12px 20px',
      textAlign: 'center',
      color: 'rgba(255,255,255,0.8)',
      fontSize: 14,
    },
    error: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      color: '#ef4444',
    },
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = media.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.header}>
        <button
          style={styles.headerBtn}
          onClick={handleDownload}
          title="Baixar"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
        >
          <Download size={20} />
        </button>
        <button
          style={styles.headerBtn}
          onClick={onClose}
          title="Fechar"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
        >
          <X size={24} />
        </button>
      </div>

      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        {error ? (
          <div style={styles.error}>
            <AlertCircle size={48} />
            <span>Mídia indisponível</span>
          </div>
        ) : media.type === 'video' ? (
          <video
            src={media.url}
            controls
            autoPlay
            style={styles.media}
            onError={() => setError(true)}
          />
        ) : (
          <img
            src={media.url}
            alt={media.caption || 'Imagem'}
            style={styles.media}
            onError={() => setError(true)}
          />
        )}
      </div>

      {media.caption && <div style={styles.caption}>{media.caption}</div>}
    </div>
  );
};

// ============================================
// MAIN ORCHESTRATOR (default export)
// ============================================

const WABusinessMessageMedia = ({ msg, isSent }) => {
  const [lightboxMedia, setLightboxMedia] = useState(null);

  const handleOpenLightbox = useCallback((media) => {
    setLightboxMedia(media);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxMedia(null);
  }, []);

  const mediaUrl = msg.media_url;
  const type = msg.type;
  const filename = msg.media_filename;
  const caption = msg.text;

  return (
    <>
      {type === 'image' && (
        <WABusinessImageMessage
          mediaUrl={mediaUrl}
          caption={caption}
          isSent={isSent}
          onOpenLightbox={handleOpenLightbox}
        />
      )}

      {type === 'video' && (
        <WABusinessVideoMessage
          mediaUrl={mediaUrl}
          caption={caption}
          isSent={isSent}
          onOpenLightbox={handleOpenLightbox}
        />
      )}

      {type === 'audio' && (
        <WABusinessAudioMessage
          mediaUrl={mediaUrl}
          isSent={isSent}
        />
      )}

      {type === 'document' && (
        <WABusinessDocumentMessage
          mediaUrl={mediaUrl}
          filename={filename}
          isSent={isSent}
        />
      )}

      <WABusinessMediaLightbox
        isOpen={!!lightboxMedia}
        onClose={handleCloseLightbox}
        media={lightboxMedia}
      />
    </>
  );
};

export default WABusinessMessageMedia;
